import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Calculator, Wine, FileText, Star, Check, ArrowRight, 
  Users, TrendingUp, Clock, Zap, Shield, HeartHandshake,
  ChevronDown, Menu, X, Sparkles, BarChart3, Smartphone
} from 'lucide-react';
import { LazyImage } from '../components/LazyImage';

export default function LandingPage() {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState('mensal');

  const features = [
    {
      icon: Calculator,
      title: 'Calculadora Inteligente',
      description: 'Calcule automaticamente quantidades e custos baseado no perfil do evento'
    },
    {
      icon: Wine,
      title: 'Catálogo de Drinks',
      description: 'Biblioteca completa com drinks clássicos e espaço para suas criações'
    },
    {
      icon: FileText,
      title: 'PDF Profissional',
      description: 'Gere orçamentos personalizados com sua marca em segundos'
    },
    {
      icon: Smartphone,
      title: 'Mobile First',
      description: 'Use de qualquer lugar, no celular, tablet ou computador'
    },
    {
      icon: BarChart3,
      title: 'Análise de Dados',
      description: 'Entenda seus eventos mais lucrativos e otimize seus preços'
    },
    {
      icon: Shield,
      title: 'Seguro e Confiável',
      description: 'Seus dados protegidos com criptografia de ponta'
    }
  ];

  const steps = [
    {
      number: '1',
      title: 'Configure seu evento',
      description: 'Adicione detalhes como número de convidados, duração e tipo de evento'
    },
    {
      number: '2',
      title: 'Selecione os drinks',
      description: 'Escolha da nossa biblioteca ou adicione suas receitas exclusivas'
    },
    {
      number: '3',
      title: 'Personalize e envie',
      description: 'Ajuste preços, adicione sua marca e envie o PDF profissional'
    }
  ];

  const testimonials = [
    {
      name: 'Carlos Silva',
      role: 'Bartender Freelancer',
      content: 'O EventPour mudou meu negócio! Consigo fazer 10x mais orçamentos em menos tempo.',
      rating: 5
    },
    {
      name: 'Ana Martins',
      role: 'Dona de Bar de Eventos',
      content: 'Profissionalizou completamente minha empresa. Clientes adoram os orçamentos detalhados.',
      rating: 5
    },
    {
      name: 'Pedro Santos',
      role: 'Equipe de Bartenders',
      content: 'A melhor ferramenta do mercado. Simples, rápida e os PDFs ficam incríveis!',
      rating: 5
    }
  ];

  const plans = {
    mensal: {
      price: 'R$ 29',
      period: '/mês',
      features: [
        'Orçamentos ilimitados',
        'Catálogo completo de drinks',
        'PDFs personalizados com sua marca',
        'Suporte via email',
        'Atualizações automáticas'
      ]
    },
    anual: {
      price: 'R$ 290',
      period: '/ano',
      savings: 'Economize R$ 58',
      features: [
        'Tudo do plano mensal',
        '2 meses grátis',
        'Suporte prioritário',
        'Acesso antecipado a novidades',
        'Backup automático'
      ]
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navbar */}
      <nav className="fixed w-full bg-white/95 backdrop-blur-sm z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <Wine className="w-8 h-8 text-purple-600" />
              <span className="text-xl font-bold">EventPour</span>
            </div>
            
            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-gray-600 hover:text-gray-900">Recursos</a>
              <a href="#how" className="text-gray-600 hover:text-gray-900">Como funciona</a>
              <a href="#pricing" className="text-gray-600 hover:text-gray-900">Preços</a>
              <button
                onClick={() => navigate('/login')}
                className="text-purple-600 hover:text-purple-700 font-medium"
              >
                Entrar
              </button>
              <button
                onClick={() => navigate('/signup')}
                className="bg-purple-600 hover:bg-purple-700 text-white px-4 py-2 rounded-lg"
              >
                Começar Grátis
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t">
            <div className="px-4 py-2 space-y-2">
              <a href="#features" className="block py-2 text-gray-600">Recursos</a>
              <a href="#how" className="block py-2 text-gray-600">Como funciona</a>
              <a href="#pricing" className="block py-2 text-gray-600">Preços</a>
              <button
                onClick={() => navigate('/login')}
                className="block w-full text-left py-2 text-purple-600"
              >
                Entrar
              </button>
              <button
                onClick={() => navigate('/signup')}
                className="block w-full bg-purple-600 text-white py-2 rounded-lg"
              >
                Começar Grátis
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 bg-gradient-to-b from-purple-50 to-white">
        <div className="max-w-7xl mx-auto text-center">
          <div className="mb-6">
            <span className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-medium">
              <Sparkles className="w-4 h-4" />
              A ferramenta #1 dos bartenders profissionais
            </span>
          </div>
          
          <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
            Crie orçamentos de bar<br />
            <span className="text-purple-600">em minutos, não horas</span>
          </h1>
          
          <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
            O EventPour calcula automaticamente quantidades, custos e gera PDFs profissionais 
            que impressionam clientes e fecham mais eventos.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/signup')}
              className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-4 rounded-lg text-lg font-medium flex items-center justify-center gap-2"
            >
              Comece Grátis
              <ArrowRight className="w-5 h-5" />
            </button>
            <button
              onClick={() => navigate('/demo')}
              className="border border-gray-300 hover:border-gray-400 px-8 py-4 rounded-lg text-lg font-medium"
            >
              Ver Demonstração
            </button>
          </div>
          
          <p className="text-sm text-gray-500 mt-4">
            7 dias grátis • Sem cartão de crédito • Cancele quando quiser
          </p>
        </div>

        {/* Hero Image */}
        <div className="max-w-5xl mx-auto mt-12 relative">
          <div className="bg-white rounded-xl shadow-2xl overflow-hidden">
            <div className="aspect-w-16 aspect-h-9 relative">
              <LazyImage 
                src="/images/eventpour_home.jpg"
                alt="Bartender profissional preparando drinks em um evento"
                className="w-full h-full object-cover"
                placeholder="data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD/2wBDAAEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQH/2wBDAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQH/wAARCAAJABADAREAAhEBAxEB/8QAHwAAAQUBAQEBAQEAAAAAAAAAAAECAwQFBgcICQoL/8QAtRAAAgEDAwIEAwUFBAQAAAF9AQIDAAQRBRIhMUEGE1FhByJxFDKBkaEII0KxwRVS0fAkM2JyggkKFhcYGRolJicoKSo0NTY3ODk6Q0RFRkdISUpTVFVWV1hZWmNkZWZnaGlqc3R1dnd4eXqDhIWGh4iJipKTlJWWl5iZmqKjpKWmp6ipqrKztLW2t7i5usLDxMXGx8jJytLT1NXW19jZ2uHi4+Tl5ufo6erx8vP09fb3+Pn6/8QAHwEAAwEBAQEBAQEBAQAAAAAAAAECAwQFBgcICQoL/8QAtREAAgECBAQDBAcFBAQAAQJ3AAECAxEEBSExBhJBUQdhcRMiMoEIFEKRobHBCSMzUvAVYnLRChYkNOEl8RcYGRomJygpKjU2Nzg5OkNERUZHSElKU1RVVldYWVpjZGVmZ2hpanN0dXZ3eHl6goOEhYaHiImKkpOUlZaXmJmaoqOkpaanqKmqsrO0tba3uLm6wsPExcbHyMnK0tPU1dbX2Nna4uPk5ebn6Onq8vP09fb3+Pn6/9oADAMBAAIRAxEAPwD+Pnw/4Zt9Qs7i5uNQvLVZLi4jjt7ZoFKRxzSRoXZ4ZCzFUBIBUDOAM5J/YM44pxeExVbDYbD0KsadWpBTqxqScoxnKKajGrBJtRTd5Sd3otD8R4c4Ow2Nw1DE4nE16UalKnOVOlKlFRlKEZNKUqU21zNK0Yq1tXqfQn/CqvDP/QV1z/v/AGX/AMjV8t/rjmv/AD4w3/gFT/5qPo/+IOZb/wA/8T/4HT/+ZD//2Q=="
              />
              <div className="absolute inset-0 bg-gradient-to-r from-purple-900/30 to-blue-900/30 mix-blend-multiply"></div>
              <div className="absolute bottom-0 left-0 right-0 p-6 bg-gradient-to-t from-black/70 to-transparent">
                <h3 className="text-white text-xl font-bold">Impressione seus clientes com orçamentos profissionais</h3>
                <p className="text-white/80 text-sm">Crie experiências memoráveis e aumente suas vendas</p>
              </div>
            </div>
          </div>
          <div className="absolute -bottom-4 right-8 bg-purple-600 text-white px-4 py-2 rounded-lg shadow-lg">
            <span className="text-sm font-medium">Usado por +500 bartenders profissionais</span>
          </div>
        </div>
      </section>

      {/* Social Proof */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-3xl font-bold text-gray-900">500+</p>
              <p className="text-gray-600">Bartenders ativos</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-gray-900">10k+</p>
              <p className="text-gray-600">Orçamentos criados</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-gray-900">R$ 2M+</p>
              <p className="text-gray-600">Em eventos fechados</p>
            </div>
            <div>
              <p className="text-3xl font-bold text-gray-900">4.9/5</p>
              <p className="text-gray-600">Avaliação média</p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Tudo que você precisa para crescer
            </h2>
            <p className="text-xl text-gray-600">
              Ferramentas profissionais que transformam seu trabalho
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="bg-white p-6 rounded-xl border border-gray-200 hover:border-purple-300 transition-colors">
                <feature.icon className="w-12 h-12 text-purple-600 mb-4" />
                <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section id="how" className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Comece a enviar orçamentos em minutos
            </h2>
            <p className="text-xl text-gray-600">
              Lance seu primeiro orçamento profissional em 3 passos simples
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {steps.map((step, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-purple-600 text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {step.number}
                </div>
                <h3 className="text-xl font-semibold mb-2">{step.title}</h3>
                <p className="text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Mais de 500 bartenders não podem estar errados
            </h2>
            <p className="text-xl text-gray-600">
              Veja o que nossos clientes dizem
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="bg-white p-6 rounded-xl shadow-lg">
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-700 mb-4">"{testimonial.content}"</p>
                <div>
                  <p className="font-semibold">{testimonial.name}</p>
                  <p className="text-sm text-gray-600">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="py-16 px-4 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Escolha seu plano
            </h2>
            <p className="text-xl text-gray-600 mb-8">
              Comece grátis e escale conforme cresce
            </p>

            {/* Plan Toggle */}
            <div className="inline-flex items-center bg-gray-200 rounded-lg p-1">
              <button
                onClick={() => setSelectedPlan('mensal')}
                className={`px-4 py-2 rounded-md transition-colors ${
                  selectedPlan === 'mensal' 
                    ? 'bg-white text-gray-900 shadow' 
                    : 'text-gray-600'
                }`}
              >
                Mensal
              </button>
              <button
                onClick={() => setSelectedPlan('anual')}
                className={`px-4 py-2 rounded-md transition-colors ${
                  selectedPlan === 'anual' 
                    ? 'bg-white text-gray-900 shadow' 
                    : 'text-gray-600'
                }`}
              >
                Anual
              </button>
            </div>
          </div>

          <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-8">
            {/* Free Plan */}
            <div className="bg-white rounded-xl border border-gray-200 p-8">
              <h3 className="text-2xl font-bold mb-2">Grátis</h3>
              <p className="text-gray-600 mb-4">Para testar e começar</p>
              <div className="mb-6">
                <span className="text-4xl font-bold">R$ 0</span>
                <span className="text-gray-600">/mês</span>
              </div>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <span>3 orçamentos por mês</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <span>Catálogo básico de drinks</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5 text-green-600" />
                  <span>PDF com marca d'água</span>
                </li>
              </ul>
              <button
                onClick={() => navigate('/signup')}
                className="w-full border border-purple-600 text-purple-600 hover:bg-purple-50 py-3 rounded-lg font-medium"
              >
                Começar Grátis
              </button>
            </div>

            {/* Pro Plan */}
            <div className="bg-purple-600 text-white rounded-xl p-8 relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <span className="bg-orange-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                  Mais Popular
                </span>
              </div>
              <h3 className="text-2xl font-bold mb-2">Profissional</h3>
              <p className="text-purple-100 mb-4">Para bartenders que querem crescer</p>
              <div className="mb-6">
                <span className="text-4xl font-bold">{plans[selectedPlan].price}</span>
                <span className="text-purple-100">{plans[selectedPlan].period}</span>
                {plans[selectedPlan].savings && (
                  <p className="text-sm text-purple-200 mt-1">{plans[selectedPlan].savings}</p>
                )}
              </div>
              <ul className="space-y-3 mb-8">
                {plans[selectedPlan].features.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-purple-200" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>
              <button
                onClick={() => navigate('/signup?plan=pro')}
                className="w-full bg-white text-purple-600 hover:bg-gray-100 py-3 rounded-lg font-medium"
              >
                Começar Teste Grátis
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 bg-purple-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Pronto para profissionalizar seu negócio?
          </h2>
          <p className="text-xl text-purple-100 mb-10">
            Junte-se a centenas de bartenders que já transformaram sua forma de trabalhar
          </p>
          <button
            onClick={() => navigate('/signup')}
            className="bg-white text-purple-600 hover:bg-gray-100 px-8 py-4 rounded-lg text-lg font-medium shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
          >
            Começar Agora - É Grátis!
          </button>
          <p className="text-sm text-purple-200 mt-4">
            Não pedimos cartão de crédito • Cancele quando quiser
          </p>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-16 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Wine className="w-8 h-8 text-purple-400" />
                <span className="text-xl font-bold text-white">EventPour</span>
              </div>
              <p className="text-sm">
                A ferramenta definitiva para bartenders profissionais
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-3">Produto</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#features" className="hover:text-white">Recursos</a></li>
                <li><a href="#pricing" className="hover:text-white">Preços</a></li>
                <li><a href="/demo" className="hover:text-white">Demonstração</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-3">Empresa</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="/sobre" className="hover:text-white">Sobre</a></li>
                <li><a href="/blog" className="hover:text-white">Blog</a></li>
                <li><a href="/contato" className="hover:text-white">Contato</a></li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold text-white mb-3">Legal</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="https://termos-de-uso-eventpour.netlify.app/" target="_blank" rel="noopener noreferrer" className="hover:text-white">Termos de Uso</a></li>
                <li><a href="https://politica-de-privacidade-eventpour.netlify.app/" target="_blank" rel="noopener noreferrer" className="hover:text-white">Política de Privacidade</a></li>
                <li><a href="https://termos-de-uso-eventpour.netlify.app/" target="_blank" rel="noopener noreferrer" className="hover:text-white">Política de Cookies</a></li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-12 pt-8 text-sm text-center">
            <p>© 2024 EventPour. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}